import React, { useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import TemplateEditor from "./TemplateEditor";
import VariablePicker from "./VariablePicker";
import TemplateVariableHelp from "./TemplateVariableHelp";

export const templateFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  html_content: z.string().min(1, "HTML content is required"),
});

export type TemplateFormValues = z.infer<typeof templateFormSchema>;

interface TemplateFormProps {
  form: UseFormReturn<TemplateFormValues>;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ form }) => {
  const editorRef = useRef<{ insertVariable?: (variable: string) => void } | null>(null);

  const handleVariableInsert = (variable: string) => {
    try {
      if (editorRef.current?.insertVariable) {
        editorRef.current.insertVariable(variable);
        return; // Variable inserted successfully
      } else {
        const currentHtml = form.getValues('html_content') || '';
        form.setValue('html_content', currentHtml + variable, { shouldValidate: true });
      }
    } catch (error) {
      console.error("Error inserting variable into HTML editor:", error);
      const currentHtml = form.getValues('html_content') || '';
      form.setValue('html_content', currentHtml + variable, { shouldValidate: true });
    }
  };

  return (
    <Form {...form}>
      <div className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Template Name</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter template name" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-6">
          <div className="flex gap-4 justify-start">
            <VariablePicker onSelectVariable={handleVariableInsert} />
            <TemplateVariableHelp />
          </div>
          
          <FormField
            control={form.control}
            name="html_content"
            render={({ field }) => (
              <FormItem>
                <FormLabel>HTML Content</FormLabel>
                <FormControl>
                  <TemplateEditor
                    htmlContent={field.value}
                    onHtmlChange={(html) => {
                      field.onChange(html);
                    }}
                    editorRef={editorRef}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </Form>
  );
};

export default TemplateForm;